import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ImageUploadMulti, fileUrl } from "@/components/ImageUpload";
import { Plus, Trash2, Pencil, Loader2, Package, Truck, CheckCircle2 } from "lucide-react";

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const toCsv = (arr) => (arr || []).join(", ");
const fromCsv = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
const blank = () => ({ name: "", description: "", priceDollars: "", category: "", sizesCsv: "", colorsCsv: "", images: [], active: true });

export function ShopAdminTab() {
  const [view, setView] = useState("items");
  return (
    <div>
      <div className="mb-4 flex gap-2">
        <button data-testid="shop-admin-items-tab" onClick={() => setView("items")}
          className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold ${view === "items" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}><Package size={15} /> Merch</button>
        <button data-testid="shop-admin-orders-tab" onClick={() => setView("orders")}
          className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold ${view === "orders" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}><Truck size={15} /> Orders</button>
      </div>
      {view === "items" ? <ItemsManager /> : <OrdersManager />}
    </div>
  );
}

function ItemsManager() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);   // form object or null
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/shop/admin/items").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = (it) => setEditing({
    id: it.id, name: it.name, description: it.description || "",
    priceDollars: it.price_cents != null ? (it.price_cents / 100).toFixed(2) : "",
    category: it.category || "", sizesCsv: toCsv(it.sizes), colorsCsv: toCsv(it.colors),
    images: it.images || [], active: it.active,
  });

  const save = async () => {
    const f = editing;
    if (!f.name.trim()) return toast.error("Name is required.");
    const price_cents = f.priceDollars === "" ? null : Math.round(parseFloat(f.priceDollars) * 100);
    if (price_cents != null && (isNaN(price_cents) || price_cents < 50)) return toast.error("Price must be at least $0.50.");
    const payload = { name: f.name.trim(), description: f.description.trim(), price_cents,
      category: f.category.trim(), sizes: fromCsv(f.sizesCsv), colors: fromCsv(f.colorsCsv),
      images: f.images, active: f.active };
    setBusy(true);
    try {
      if (f.id) { await api.put(`/shop/admin/items/${f.id}`, { ...payload, clear_price: price_cents == null }); toast.success("Item updated"); }
      else { await api.post("/shop/admin/items", payload); toast.success("Item added"); }
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/shop/admin/items/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  if (editing) {
    const f = editing, upd = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
    return (
      <div className="space-y-3 rounded-xl border border-border bg-card p-4">
        <p className="font-display text-lg font-bold text-primary">{f.id ? "Edit Item" : "Add Merch Item"}</p>
        <Lbl l="Name"><input data-testid="shop-item-name" value={f.name} onChange={(e) => upd("name", e.target.value)} className={inp} /></Lbl>
        <Lbl l="Description"><textarea data-testid="shop-item-desc" value={f.description} onChange={(e) => upd("description", e.target.value)} rows={3} className={inp} /></Lbl>
        <div className="flex gap-3">
          <div className="flex-1"><Lbl l="Price (USD)"><input data-testid="shop-item-price" value={f.priceDollars} onChange={(e) => upd("priceDollars", e.target.value)} placeholder="25.00" inputMode="decimal" className={inp} /></Lbl></div>
          <div className="flex-1"><Lbl l="Category"><input data-testid="shop-item-category" value={f.category} onChange={(e) => upd("category", e.target.value)} placeholder="Apparel" className={inp} /></Lbl></div>
        </div>
        <Lbl l="Available Sizes (comma separated, leave blank if N/A)"><input data-testid="shop-item-sizes" value={f.sizesCsv} onChange={(e) => upd("sizesCsv", e.target.value)} placeholder="S, M, L, XL, 2XL" className={inp} /></Lbl>
        <Lbl l="Available Colors (comma separated, leave blank if N/A)"><input data-testid="shop-item-colors" value={f.colorsCsv} onChange={(e) => upd("colorsCsv", e.target.value)} placeholder="Black, Heather Gray, Navy" className={inp} /></Lbl>
        <ImageUploadMulti label="Product Photos" value={f.images} onChange={(v) => upd("images", v)} testid="shop-item-images" />
        <label className="flex items-center gap-2 text-sm font-semibold text-primary">
          <input data-testid="shop-item-active" type="checkbox" checked={f.active} onChange={(e) => upd("active", e.target.checked)} /> Visible in shop
        </label>
        <div className="flex gap-2 pt-1">
          <button data-testid="shop-item-save" onClick={save} disabled={busy} className="flex h-11 flex-1 items-center justify-center gap-2 rounded-full bg-primary font-semibold text-primary-foreground disabled:opacity-60">
            {busy && <Loader2 size={16} className="animate-spin" />} Save
          </button>
          <button data-testid="shop-item-cancel" onClick={() => setEditing(null)} className="h-11 rounded-full border border-border px-5 font-semibold text-muted-foreground">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button data-testid="shop-add-item-btn" onClick={() => setEditing(blank())}
        className="mb-4 flex h-11 items-center gap-2 rounded-full bg-primary px-4 font-semibold text-primary-foreground"><Plus size={17} /> Add Item</button>
      {items === null ? <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-muted-foreground" /></div>
        : items.length === 0 ? <p className="text-sm text-muted-foreground">No merch items yet.</p>
        : (
          <div className="space-y-2">
            {items.map((it) => (
              <div key={it.id} data-testid={`shop-admin-item-${it.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-muted">
                  {(it.images || [])[0] ? <img src={fileUrl(it.images[0])} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-muted-foreground"><Package size={18} /></div>}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-primary">{it.name} {!it.active && <span className="ml-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-bold uppercase text-muted-foreground">Hidden</span>}</p>
                  <p className="text-xs text-muted-foreground">{money(it.price_cents)}{it.sizes?.length ? ` · ${it.sizes.length} sizes` : ""}{it.colors?.length ? ` · ${it.colors.length} colors` : ""} · {it.paid_orders || 0} sold</p>
                </div>
                <button data-testid={`shop-edit-${it.id}`} onClick={() => startEdit(it)} className="grid h-9 w-9 place-items-center rounded-full border border-border text-primary"><Pencil size={15} /></button>
                <button data-testid={`shop-delete-${it.id}`} onClick={() => del(it.id)} className="grid h-9 w-9 place-items-center rounded-full border border-border text-destructive"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

function OrdersManager() {
  const [orders, setOrders] = useState(null);
  const load = useCallback(() => { api.get("/shop/admin/orders").then((r) => setOrders(r.data.orders)).catch(() => setOrders([])); }, []);
  useEffect(() => { load(); }, [load]);

  const toggleFulfill = async (o) => {
    try {
      const tracking = !o.fulfilled ? (window.prompt("Tracking number (optional):", o.tracking || "") ?? "") : o.tracking;
      await api.post(`/shop/admin/orders/${o.order_id}/fulfill`, { fulfilled: !o.fulfilled, tracking });
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  if (orders === null) return <div className="grid place-items-center py-16"><Loader2 className="animate-spin text-muted-foreground" /></div>;
  const paid = orders.filter((o) => o.payment_status === "paid");
  if (paid.length === 0) return <p className="text-sm text-muted-foreground">No paid orders yet.</p>;

  return (
    <div className="space-y-3">
      {paid.map((o) => (
        <div key={o.order_id} data-testid={`shop-order-${o.order_id}`} className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-semibold text-primary">{o.customer_name} · {money(o.amount_cents)}</p>
              <p className="text-xs text-muted-foreground">{o.email}{o.phone ? ` · ${o.phone}` : ""} · {new Date(o.created_at).toLocaleDateString()}</p>
            </div>
            {o.fulfilled
              ? <span className="flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-[11px] font-bold text-green-700"><CheckCircle2 size={13} /> Fulfilled</span>
              : <span className="rounded-full bg-amber-100 px-2 py-1 text-[11px] font-bold text-amber-700">To Ship</span>}
          </div>
          <div className="mt-2 rounded-lg bg-muted/50 p-2 text-sm">
            {(o.items || []).map((l, i) => <div key={i} className="flex justify-between py-0.5"><span>{l.quantity}× {l.label || l.name}</span><span>{money(l.line_total_cents)}</span></div>)}
            <div className="mt-1 flex justify-between border-t border-border pt-1 text-xs text-muted-foreground"><span>Shipping ({o.item_count} item{o.item_count === 1 ? "" : "s"})</span><span>{money(o.shipping_cents)}</span></div>
          </div>
          <div className="mt-2 text-sm">
            <p className="font-semibold text-primary">Ship to:</p>
            <p className="text-muted-foreground">{o.shipping?.name}<br />{o.shipping?.line1}{o.shipping?.line2 ? `, ${o.shipping.line2}` : ""}<br />{o.shipping?.city}, {o.shipping?.state} {o.shipping?.postal_code}</p>
          </div>
          {o.tracking && <p className="mt-1 text-xs text-muted-foreground">Tracking: {o.tracking}</p>}
          <button data-testid={`shop-fulfill-${o.order_id}`} onClick={() => toggleFulfill(o)}
            className={`mt-3 h-10 w-full rounded-full text-sm font-semibold ${o.fulfilled ? "border border-border text-muted-foreground" : "bg-secondary text-white"}`}>
            {o.fulfilled ? "Mark as Not Shipped" : "Mark as Shipped"}
          </button>
        </div>
      ))}
    </div>
  );
}

const inp = "mt-1 w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm";
const Lbl = ({ l, children }) => (<div><label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{l}</label>{children}</div>);
